// TODO: Re-enable full edit (including webhook URL) once backend PUT handler supports updating email/webhook values
// Currently the modal only allows editing severity threshold, which the backend does support
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { CellAlignment, Column } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { NOTIFICATION_TABLE_COLUMN_WIDTHS } from '@/sections/settings/components/cards/notifications/constants';
import { SeverityThresholdCell } from '@/sections/settings/components/cells/SeverityThresholdCell';
import { RowActionsMenu } from '@/sections/settings/components/primitives/RowActionsMenu';
import { Account } from '@/types';

interface WebhookColumnsConfig {
  /** Handler for viewing webhook configuration (severity-only editing) */
  onView: (webhook: Account) => void;
  /** Handler for deleting a webhook */
  onDelete: (webhook: Account) => void;
}

/**
 * Column definitions for Webhook Notifications TanStackTable.
 *
 * Provides:
 * - Webhook Name column showing user-defined webhook name
 * - Severity Threshold column with badge and tooltip
 * - Actions column with ellipsis menu (Edit, Delete)
 */
export function getWebhookColumns({
  onView,
  onDelete,
}: WebhookColumnsConfig): Column<Account>[] {
  return [
    {
      id: 'value',
      label: 'Webhook Name',
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.primaryValue,
      cell: item => (
        <span className="text-chariot-text-primary truncate text-sm">
          {item.value || 'Webhook configured'}
        </span>
      ),
    },
    {
      id: 'severities' as keyof Account,
      label: (
        <span className="flex items-center gap-1">
          Severity Threshold
          <Tooltip title="Minimum vulnerability severity level that will trigger webhook notification">
            <InformationCircleIcon className="text-chariot-text-secondary size-4 shrink-0 cursor-pointer" />
          </Tooltip>
        </span>
      ),
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.severities,
      cell: item => {
        const threshold =
          (item.config?.severities as string) ||
          item.settings?.severityThreshold ||
          'MHC';
        return (
          <SeverityThresholdCell
            threshold={threshold}
            data-testid={`severity-${item.key}`}
          />
        );
      },
    },
    {
      id: '' as keyof Account,
      label: '',
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.actions,
      align: 'right' as CellAlignment,
      cell: item => (
        <div className="flex justify-end">
          <RowActionsMenu
            testIdPrefix="webhook"
            itemKey={item.key}
            actions={[
              {
                label: 'Edit',
                icon: <PencilIcon className="size-4" />,
                onClick: () => onView(item),
              },
              {
                label: 'Delete',
                icon: <TrashIcon className="size-4" />,
                onClick: () => onDelete(item),
                variant: 'danger',
              },
            ]}
          />
          {/* TODO: Re-enable full edit option once backend PUT handler supports updating email/webhook values
              See: backend/pkg/handler/handlers/account/crud/crud.go - PUT only updates Settings, not Value */}
        </div>
      ),
    },
  ];
}
